import { Location } from "@angular/common";
import { Injectable, OnDestroy } from "@angular/core";
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    Params,
    Router,
} from "@angular/router";
import { Observable, Subscription } from "rxjs";

import { BackToGridService } from "@hermes/fragments/product-utils";
import { LocaleUrlPrefix } from "@hermes/locale";
import {
    FeatureFlagFacade,
    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
} from "@hermes/states/flipper";
import { skuToUrlSku, urlSkuToSku } from "@hermes/utils-generic/helpers";

import { getFacetsQueryParameters } from "../../helpers/criterias-helper/criterias.helper";
import { DEFAULT_SORT, Facet } from "../../model/grid.model";

/**
 * Represents datas stored in URL
 */
export interface GridUrlData {
    facet: Facet;
    fhParams: string | undefined;
    sort: string | undefined;
    searchTerm?: string;
    positionSku?: string;
}

/**
 * A service who manage URL hash because we have to save some data into URL :
 * - fhParams corresponding to the FH params for the current filtered grid
 * - sort corresponding to the current sort applied
 * - open filters corresponding to the current filter groups opened
 * - positionSku corresponding to the result item clicked
 */
@Injectable()
export class UrlManagerService implements OnDestroy {
    private isCombinedFacetActivated = false;

    private subscription = new Subscription();

    private isCombinedFacetEnabled$: Observable<boolean> =
        this.featureFlagFacade.isActivated(
            SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
        );

    constructor(
        private location: Location,
        private backToGridService: BackToGridService,
        private featureFlagFacade: FeatureFlagFacade,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        this.subscription.add(
            this.isCombinedFacetEnabled$.subscribe((flag) => {
                this.isCombinedFacetActivated = flag;
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Build grid page URL with given datas
     *
     * @returns a grid page URL
     */
    public buildUrl(urlData: GridUrlData): string {
        const stringifyQueryParameters = Object.entries(
            this.route.snapshot.queryParams,
        )
            .filter(([key, _value]) => !key.includes("facet_")) // Remove existing facet
            .filter(([key, _value]) => key !== "s") // Remove search parameter
            .concat(urlData.searchTerm ? [["s", urlData.searchTerm]] : []) // restore search param
            .concat(getFacetsQueryParameters(urlData.facet.combinedFacets));

        const queryString = stringifyQueryParameters
            .map(([key, value]) => `${key}=${value}`) // building string for queryParams
            .join("&");

        const pathName = decodeURIComponent(this.location.path());
        const [pathWithoutQueryParameters] = pathName.split("?");

        const fragment = this.buildFragment(urlData);

        if (queryString) {
            return `${pathWithoutQueryParameters}?${queryString}${fragment}`;
        }

        return `${pathWithoutQueryParameters}${fragment}`;
    }

    /**
     * Update url fragment with given url datas
     */
    public updateUrl(urlData: GridUrlData): void {
        this.location.replaceState(this.buildUrl(urlData));
    }

    /**
     * Add positionSku in URL fragment
     */
    public savePositionSku(sku: string): void {
        // add positionSku to new Url
        const newUrl = `${this.location.path(true)}|${skuToUrlSku(sku)}`;
        // save URL into session storage for back to grid
        this.backToGridService.storeGridUrl(newUrl);
        // save URL in browser history
        this.location.replaceState(newUrl);
    }

    /**
     * This method create an fragment as string based on given criteria.
     * The fragment is a three parts string  as [ urlParams | sort ]
     */
    public buildFragment({ facet, fhParams, sort }: GridUrlData): string {
        if ((fhParams && fhParams.length > 0) || (sort && sort?.length > 0)) {
            const builtFragment = [
                // first fragment url params from merchandising
                facet?.enabled ? "" : fhParams,

                // second fragment sort
                sort && sort !== DEFAULT_SORT ? sort : undefined,
            ].join("|");
            return `#${builtFragment}`;
        }

        return "";
    }

    public parseRoute(
        route: ActivatedRouteSnapshot,
    ): Pick<GridUrlData, "facet"> {
        return {
            ...this.parseQueryParams(route.queryParams || {}),
            ...this.parseFragment(route.fragment || ""),
        };
    }

    /**
     * Remove all query parameters to keep baseUrl.
     */
    public getUrlWithoutParams(localUrlPrefix: LocaleUrlPrefix) {
        const urlTree = this.router.parseUrl(this.location.path());
        urlTree.queryParams = {};
        const containsTrailingSlash = urlTree.toString().charAt(0) === "/";

        return `${localUrlPrefix}${
            containsTrailingSlash ? "" : "/"
        }${urlTree.toString()}`;
    }

    private parseQueryParams(
        queryParameters: Params,
    ): Pick<GridUrlData, "facet"> {
        const combinedFacets = Object.entries(queryParameters)
            .filter(([key]) => key.startsWith("facet_"))
            .map(([facetName, facetNameValue]) => ({
                facetName,
                facetNameValue,
            }));

        const facetParameters = Object.keys(queryParameters).find((key) =>
            key.startsWith("facet_"),
        );

        const facet = {
            facetName: facetParameters || "",
            facetNameValue: facetParameters
                ? queryParameters[facetParameters]
                : "",
            combinedFacets: facetParameters ? combinedFacets : [],
            facetDisplayName: "",
            facetTitle: "",
            updateMetas: !!facetParameters,
            enabled: !!facetParameters,
            path: "",
        };

        return { facet };
    }

    /**
     * Retrieve some data from fragment in grid URL.
     *
     * Trivia : Grid fragment is a three parts string following this pattern `"urlParams | sort | positionSku"`
     */
    private parseFragment(fragment: string): Omit<GridUrlData, "facet"> {
        const [fhParameters, sort, sku] = fragment.split("|");

        const positionSku = sku ? urlSkuToSku(sku) : undefined;

        return {
            fhParams: fhParameters,
            sort,
            positionSku,
        };
    }
}
